import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Menu, MenuItem, Typography, useMediaQuery } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Chip, ChipToggleGroup, EmptyState, IconButton, PhotoGridTile, PromoBanner, Skeleton } from '@/design-system'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { EgowallLogo } from '../../components/EgowallLogo'
import { AlbumHero } from '../../components/AlbumHero'
import { UploadDropzone } from '../../components/UploadDropzone'
import { ShareSheet } from '../../components/ShareSheet'
import { TurnTo3dCta } from '../../components/TurnTo3dCta'
import { Lightbox } from '../../components/Lightbox'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { useAlbumDetail } from '../../hooks/useAlbumDetail'
import { useAlbumUpload } from '../../hooks/useAlbumUpload'
import { useAlbumLike } from '../../hooks/useAlbumLike'
import { useAlbumDelete } from '../../hooks/useAlbumDelete'
import { useCreateGallery } from '../../hooks/useCreateGallery'
import { useLocalStorage } from '@/hooks/common'
import { useUpdateTopic, useGetPermissions, useUpdateVisibility } from '@/hooks/api/dashboard'
import { useFeatureGate, FEATURE_KEYS } from '@/context/SubscriptionContext'
import { fireAlertToast } from '@/design-system/alerts'
import { PrivacyType, ResourceType } from '@/utils/constants'
import {
  withAllowDownload,
  withCoverEgoId,
  withFullResolutionUploads,
  withGalleryId,
  withPhotoOrder,
} from '../../utils/albumXData'
import { UPLOAD_RESIZE_DIM } from '@/lib/featureKeys'
import { AlbumPhoto } from '../../types'
import { formatRelative } from '../../utils/date'

type PhotoFilter = 'all' | 'most-loved' | 'with-comments'

const TopBar = styled(Box)({
  height: 60,
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '0 24px',
  position: 'sticky',
  top: 0,
  zIndex: 40,
  background: 'color-mix(in srgb, var(--ds-canvas) 88%, transparent)',
  backdropFilter: 'blur(12px)',
  borderBottom: '1px solid var(--ds-border)',
  '@media (max-width: 640px)': {
    gap: 6,
    padding: '0 12px',
  },
})

const Crumb = styled(Typography)({
  fontSize: 13,
  color: 'var(--ds-ink-subtle)',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minWidth: 0,
  overflow: 'hidden',
  '& b': { color: 'var(--ds-ink)', fontWeight: 650 },
  '& span': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
})

const PresenceChip = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  flexShrink: 0,
  fontFamily: 'var(--ds-font-mono, "IBM Plex Mono", monospace)',
  fontSize: 9.5,
  letterSpacing: '0.1em',
  color: 'var(--ds-ink-subtle)',
  whiteSpace: 'nowrap',
  '& i': {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: 'var(--ds-cyan, #01dafe)',
    boxShadow: '0 0 8px var(--ds-cyan, #01dafe)',
    flexShrink: 0,
    fontStyle: 'normal',
    display: 'block',
  },
})

const HeroTitle = styled(Typography)({
  color: '#fff',
  fontSize: 'clamp(28px, 4vw, 44px)',
  fontWeight: 740,
  letterSpacing: '-0.04em',
  lineHeight: 1.05,
})

const HeroSub = styled(Box)({
  display: 'flex',
  gap: 16,
  alignItems: 'center',
  marginTop: 8,
  flexWrap: 'wrap',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 10,
  letterSpacing: '0.1em',
  color: 'rgba(255,255,255,0.66)',
})

const ToolRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  padding: '22px 0',
  maxWidth: 1180,
  margin: '0 auto',
})

const OwnerNote = styled(Typography)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  letterSpacing: '0.12em',
  color: 'var(--ds-ink-subtle)',
  textTransform: 'uppercase',
})

const ReorderHint = styled(Typography)({
  fontSize: 13,
  color: 'var(--ds-ink-subtle)',
})

const BodyWrap = styled(Box)({
  maxWidth: 1180,
  margin: '0 auto',
  padding: '0 32px 70px',
})

// CSS multi-column flows items column-major (fills column 1 top-to-bottom
// before starting column 2, balancing by column *height*) — for exactly 4
// photos that lands as an uneven 2-1-1 split across 3 columns instead of a
// clean 3-then-1. A real grid is row-major, so items fill left-to-right,
// top-to-bottom instead — 3 across on a row wide enough, however many fit
// wrap to the next row. `auto-fit` (not `auto-fill`) collapses unused
// column tracks, so a lone photo or a short last row gets real width
// instead of a narrow 280px column with empty tracks beside it.
const MasonryGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: 16,
  alignItems: 'start',
})

const PROMO_OPTIONS = [
  { value: 'all', label: 'All photos' },
  { value: 'most-loved', label: 'Most loved' },
  { value: 'with-comments', label: 'With comments' },
]

export function AlbumViewScreen() {
  const { albumId = '' } = useParams()
  const navigate = useNavigate()
  const isCompact = useMediaQuery('(max-width:640px)')
  const { album, setAlbum, loading, error, refresh } = useAlbumDetail(albumId)
  const updateTopic = useUpdateTopic(albumId || '')
  const getPermissions = useGetPermissions()
  const updateVisibility = useUpdateVisibility()
  const [photos, setPhotos] = useState<AlbumPhoto[]>([])
  const [photoFilter, setPhotoFilter] = useState<PhotoFilter>('all')
  const [shareOpen, setShareOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [photoToDelete, setPhotoToDelete] = useState<AlbumPhoto | null>(null)
  const [albumDeleteOpen, setAlbumDeleteOpen] = useState(false)
  const [showUpload, setShowUpload] = useState(false)
  const [reorderMode, setReorderMode] = useState(false)
  const [dragFromId, setDragFromId] = useState<string | null>(null)
  const [dragOverId, setDragOverId] = useState<string | null>(null)

  useEffect(() => {
    if (album?.photos) setPhotos(album.photos)
  }, [album?.photos])

  useEffect(() => {
    if (lightboxIndex == null) return
    if (photos.length === 0) {
      setLightboxIndex(null)
      return
    }
    if (lightboxIndex >= photos.length) {
      setLightboxIndex(photos.length - 1)
    }
  }, [photos, lightboxIndex])

  const silentRefresh = useCallback(() => refresh({ silent: true }), [refresh])
  const { uploadFiles, uploading, progress } = useAlbumUpload(albumId, silentRefresh)
  const { toggleLike } = useAlbumLike(albumId, photos, setPhotos)

  const [ewUser] = useLocalStorage('ewUser', null)
  const isOwner =
    album?.isOwner === true ||
    (Boolean(ewUser?.userID) &&
      Boolean(album?.ownerId) &&
      String(ewUser.userID) === String(album?.ownerId))
  const { deletePhoto, deleteAlbum, siblingsFor, deletingPhoto, deletingAlbum } = useAlbumDelete(
    albumId,
    photos,
    setPhotos,
    {
      isOwner,
      onAlbumDeleted: () => navigate('/albums', { replace: true }),
    },
  )

  const shareUrl = useMemo(() => {
    if (typeof window === 'undefined') return ''
    return `${window.location.origin}/a/${albumId}`
  }, [albumId])

  const onAllowDownloadChange = useCallback(
    async (next: boolean) => {
      if (!album || !albumId) return
      const nextXData = withAllowDownload(album.topicXData, next)
      try {
        await updateTopic.mutateAsync({
          topicName: album.title,
          topicDescription: album.description || '',
          topicXData: nextXData,
        })
        setAlbum({ ...album, allowDownload: next, topicXData: nextXData })
        fireAlertToast(next ? 'share.downloadOn' : 'share.downloadOff')
      } catch {
        fireAlertToast('share.downloadUpdateFailed')
        throw new Error('download setting update failed')
      }
    },
    [album, albumId, updateTopic, setAlbum],
  )

  const onSetCover = useCallback(
    async (photo: AlbumPhoto) => {
      if (!album || !albumId || !photo.egoId) return
      const nextXData = withCoverEgoId(album.topicXData, photo.egoId)
      try {
        await updateTopic.mutateAsync({
          topicName: album.title,
          topicDescription: album.description || '',
          topicXData: nextXData,
        })
        setAlbum({
          ...album,
          coverEgoId: photo.egoId,
          coverUrl: photo.url || photo.thumbUrl || album.coverUrl,
          topicXData: nextXData,
        })
        fireAlertToast('share.coverUpdated')
      } catch {
        fireAlertToast('share.coverUpdateFailed')
        throw new Error('cover update failed')
      }
    },
    [album, albumId, updateTopic, setAlbum],
  )

  /**
   * Drag-to-reorder (task #10). There's no backend "reorder this album"
   * call — a photo's position comes from which upload-batch note it's in
   * plus its slot in that note's own `egoList` (mappers.ts), and no single
   * field spans every note to rewrite instead. Same FE-owned-JSON
   * workaround `coverEgoId`/`allowDownload` already use: the full egoId
   * order lives in `topicXData.photoOrder`, applied by `sortPhotosByOrder`
   * wherever an album loads (owner's own view here, and the guest view via
   * the same `topicToAlbumDetail`) — so a reorder here is instantly visible
   * to anyone else viewing the album too, not just a local sort.
   */
  const commitReorder = useCallback(
    async (fromId: string, toId: string) => {
      if (!album || !albumId || fromId === toId) return
      const prevPhotos = photos
      const fromIdx = photos.findIndex((p) => p.id === fromId)
      const toIdx = photos.findIndex((p) => p.id === toId)
      if (fromIdx === -1 || toIdx === -1) return
      const next = [...photos]
      const [moved] = next.splice(fromIdx, 1)
      next.splice(toIdx, 0, moved)
      setPhotos(next)

      const nextOrder = next.map((p) => p.egoId).filter((id): id is string => Boolean(id))
      const nextXData = withPhotoOrder(album.topicXData, nextOrder)
      try {
        await updateTopic.mutateAsync({
          topicName: album.title,
          topicDescription: album.description || '',
          topicXData: nextXData,
        })
        setAlbum({ ...album, topicXData: nextXData })
      } catch {
        setPhotos(prevPhotos)
        fireAlertToast('share.reorderUpdateFailed')
      }
    },
    [album, albumId, photos, updateTopic, setAlbum],
  )

  const createGallery = useCreateGallery()
  const { isFeatureAvailable, showUpgrade } = useFeatureGate()

  const onFullResolutionUploadsChange = useCallback(
    async (next: boolean) => {
      if (!album || !albumId) return
      const nextXData = withFullResolutionUploads(album.topicXData, next)
      try {
        await updateTopic.mutateAsync({
          topicName: album.title,
          topicDescription: album.description || '',
          topicXData: nextXData,
        })
        setAlbum({ ...album, fullResolutionUploads: next, topicXData: nextXData })
        fireAlertToast(next ? 'share.uploadQualityOn' : 'share.uploadQualityOff')
      } catch {
        fireAlertToast('share.uploadQualityUpdateFailed')
        throw new Error('upload quality update failed')
      }
    },
    [album, albumId, updateTopic, setAlbum],
  )

  // Owner's stated preference AND live tier both have to hold at upload time
  // — a lapsed subscription silently falls back to 1000px, no error/toast
  // (see heic-and-full-resolution-upload-spec.md §B6/§B9).
  const uploadMaxDim =
    album?.fullResolutionUploads && isFeatureAvailable(FEATURE_KEYS.FULL_RESOLUTION_UPLOAD)
      ? UPLOAD_RESIZE_DIM.full
      : UPLOAD_RESIZE_DIM.standard

  /**
   * "Turn into 3D" used to route through a 7-step wizard (scene/frame/color/
   * material/prints/matting/review) before landing on Studio. That wizard's
   * picks seeded a real pre-arranged Classic template, but for most owners
   * the extra steps were pure friction before reaching the same
   * destination. This creates the gallery stub with the album already
   * linked (topicXData.sourceAlbumId) and jumps straight to Studio —
   * useStudio3DData sees a real sourceAlbumId with no saved scene and
   * auto-lays the album's own photos into the room via autoLayoutArts, so
   * it opens populated instead of empty, no wizard required.
   *
   * The wizard was also the app's one real hard paywall (GALLERY_3D) — an
   * existing gallery link always skips it below, same as before, but a
   * fresh create must still gate here or free tier loses its only hard
   * block.
   */
  const handleTurnTo3D = useCallback(async () => {
    if (!album || !albumId || !isOwner) return
    if (album.galleryId) {
      navigate(`/galleries/${album.galleryId}/studio`)
      return
    }
    if (!isFeatureAvailable(FEATURE_KEYS.GALLERY_3D)) {
      showUpgrade({ featureKey: FEATURE_KEYS.GALLERY_3D })
      return
    }
    try {
      const galleryId = await createGallery.create(album.title, albumId)
      const nextXData = withGalleryId(album.topicXData, galleryId)
      try {
        await updateTopic.mutateAsync({
          topicName: album.title,
          topicDescription: album.description || '',
          topicXData: nextXData,
        })
        setAlbum({ ...album, galleryId, topicXData: nextXData })
      } catch {
        /* non-critical — the gallery itself was created fine, just retry the link next visit */
      }
      fireAlertToast('share.turnTo3dCreated')
      navigate(`/galleries/${galleryId}/studio`)
    } catch {
      fireAlertToast('share.turnTo3dFailed')
    }
  }, [album, albumId, isOwner, createGallery, updateTopic, setAlbum, navigate, isFeatureAvailable, showUpgrade])

  const togglePrivacy = useCallback(async () => {
    if (!album || !albumId) return
    const newPrivacy = album.privacy === 'private' ? PrivacyType.PUBLIC : PrivacyType.PRIVATE
    try {
      const permData = await getPermissions.mutateAsync(albumId)
      await updateVisibility.mutateAsync({
        resourceID: albumId,
        cliqueTypes: album.cliqueTypes || [],
        cliques: [],
        connections: [],
        accessTypeID: newPrivacy.toString(),
        permissionID: permData.permissionID,
        resourceType: ResourceType.TOPIC,
      })
      setAlbum({ ...album, privacy: newPrivacy === PrivacyType.PUBLIC ? 'public' : 'private' })
      fireAlertToast(newPrivacy === PrivacyType.PUBLIC ? 'share.madePublic' : 'share.madePrivate')
    } catch {
      fireAlertToast('share.privacyUpdateFailed')
      throw new Error('privacy update failed')
    }
  }, [album, albumId, getPermissions, updateVisibility, setAlbum])

  const title = album?.title || 'Album'
  const batchCount = photoToDelete ? siblingsFor(photoToDelete).length : 1
  const commentedCount = useMemo(() => photos.filter((p) => (p.commentCount || 0) > 0).length, [photos])
  const totalLikes = useMemo(() => photos.reduce((sum, p) => sum + (p.likeCount || 0), 0), [photos])
  const updatedRelative = formatRelative(album?.updatedAt)

  const visiblePhotos = useMemo(() => {
    if (photoFilter === 'most-loved') return [...photos].sort((a, b) => (b.likeCount || 0) - (a.likeCount || 0))
    if (photoFilter === 'with-comments') return photos.filter((p) => (p.commentCount || 0) > 0)
    return photos
  }, [photos, photoFilter])

  const filterOptions = useMemo(
    () =>
      PROMO_OPTIONS.map((o) =>
        o.value === 'with-comments' ? { ...o, label: `With comments · ${commentedCount}` } : o,
      ),
    [commentedCount],
  )

  const openLightbox = (photo: AlbumPhoto) => {
    const i = photos.indexOf(photo)
    if (i < 0) return
    setLightboxIndex(i)
  }

  const bumpCommentCount = (noteId: string) => {
    setPhotos((prev) =>
      prev.map((p) =>
        p.noteId === noteId ? { ...p, commentCount: (p.commentCount || 0) + 1 } : p,
      ),
    )
  }

  const requestDeletePhoto = (photo: AlbumPhoto) => {
    setPhotoToDelete(photo)
  }

  const confirmDeletePhoto = async () => {
    if (!photoToDelete) return
    const ok = await deletePhoto(photoToDelete)
    if (ok) {
      setPhotoToDelete(null)
      // Keep lightbox open on next photo if any remain
    }
  }

  const confirmDeleteAlbum = async () => {
    const ok = await deleteAlbum()
    if (ok) setAlbumDeleteOpen(false)
  }

  if (loading && !album) {
    return (
      <Box>
        <Skeleton height={60} sx={{ borderRadius: 0 }} />
        <Skeleton height={380} sx={{ borderRadius: 0 }} />
        <BodyWrap>
          <MasonryGrid sx={{ pt: 4 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} sx={{ aspectRatio: '4/3', width: '100%' }} rounded='lg' />
            ))}
          </MasonryGrid>
        </BodyWrap>
      </Box>
    )
  }

  if (error || !album) {
    return (
      <Box p={4}>
        <EmptyState
          title='Album not found'
          description={error || 'This album may have been removed.'}
          actionLabel='Back to albums'
          onAction={() => navigate('/albums')}
        />
      </Box>
    )
  }

  return (
    <Box>
      <TopBar>
        <EgowallLogo hideText onClick={() => navigate('/albums')} />
        <Crumb>
          <Box component='span' onClick={() => navigate('/albums')} sx={{ cursor: 'pointer' }}>
            Albums
          </Box>
          {' / '}
          <b>
            <span>{title}</span>
          </b>
        </Crumb>
        <Box sx={{ flex: 1 }} />
        {!!album.viewerCount && !isCompact && (
          <PresenceChip>
            <i />
            {album.viewerCount} INSIDE NOW
          </PresenceChip>
        )}
        <Button
          variant='secondary'
          size='sm'
          onClick={() => setShareOpen(true)}
          aria-label='Share'
          sx={{
            flexShrink: 0,
            px: isCompact ? 0 : undefined,
            minWidth: isCompact ? 34 : undefined,
            width: isCompact ? 34 : undefined,
          }}
        >
          {isCompact ? '⚿' : '⚿ Share'}
        </Button>
        <TurnTo3dCta
          visible={isOwner && photos.length > 0}
          compact={isCompact}
          size='sm'
          disabled={createGallery.isLoading}
          onClick={handleTurnTo3D}
        />
        {isOwner && (
          <IconButton
            tone='quiet'
            aria-label='Album options'
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{ flexShrink: 0 }}
          >
            <MoreHorizIcon />
          </IconButton>
        )}
      </TopBar>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 180,
            borderRadius: 'var(--ds-radius-md)',
            boxShadow: 'var(--ds-shadow-soft)',
            border: '1px solid var(--ds-border)',
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null)
            setShareOpen(true)
          }}
          sx={{ fontSize: 14 }}
        >
          Share album
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null)
            setAlbumDeleteOpen(true)
          }}
          sx={{ fontSize: 14, color: 'var(--ds-danger)' }}
        >
          Delete album
        </MenuItem>
      </Menu>

      <AlbumHero
        photos={photos}
        coverEgoId={album.coverEgoId}
        variant='owner'
        badge={
          totalLikes > 0 ? (
            // Chip's flex `gap` applies between every child — `{totalLikes} loved`
            // used to be two separate JSX children (expression + text node), so
            // it landed the same gap both after the ❤ icon *and* between the
            // count and "loved", widening that second, unwanted gap. One string
            // keeps it a single child — exactly one gap, right after the icon.
            <Chip tone='night' icon='❤'>{`${totalLikes} loved`}</Chip>
          ) : undefined
        }
      >
        <HeroTitle noWrap>{title}</HeroTitle>
        <HeroSub>
          <span>
            {photos.length} PHOTO{photos.length === 1 ? '' : 'S'} · FULL RES
          </span>
          <span>{album.privacy === 'public' ? 'PUBLIC' : 'PRIVATE'}</span>
          {updatedRelative && <span>UPDATED {updatedRelative.toUpperCase()}</span>}
        </HeroSub>
      </AlbumHero>

      <BodyWrap>
        {photos.length === 0 ? (
          <Box pt={4}>
            {isOwner && (
              <Box mb={3}>
                <UploadDropzone onFiles={uploadFiles} disabled={uploading} progress={progress} />
              </Box>
            )}
            <EmptyState
              title={isOwner ? 'Add your first photos' : 'No photos yet'}
              description={
                isOwner
                  ? 'Drop images above — full resolution, yours to share.'
                  : 'The owner hasn’t added photos yet.'
              }
            />
          </Box>
        ) : (
          <>
            <ToolRow sx={{ margin: 0, px: 0 }}>
              {reorderMode ? (
                <ReorderHint>Drag photos to reorder — drop to save.</ReorderHint>
              ) : (
                <ChipToggleGroup
                  options={filterOptions}
                  value={photoFilter}
                  onChange={(v) => setPhotoFilter(v as PhotoFilter)}
                />
              )}
              <Box sx={{ flex: 1 }} />
              {isOwner && (
                <>
                  {!reorderMode && (
                    <Chip
                      tone={showUpload || uploading ? 'live' : 'neutral'}
                      onClick={() => {
                        if (uploading) return
                        setShowUpload((v) => !v)
                      }}
                      sx={{ cursor: uploading ? 'default' : 'pointer' }}
                    >
                      ＋ Add photos
                    </Chip>
                  )}
                  {photos.length > 1 && (
                    <Chip
                      tone={reorderMode ? 'live' : 'neutral'}
                      onClick={() => {
                        // Reordering a filtered subset doesn't map cleanly onto
                        // the full album order — force "All photos" while active.
                        if (!reorderMode) setPhotoFilter('all')
                        setReorderMode((v) => !v)
                      }}
                    >
                      {reorderMode ? '✓ Done' : '⇅ Reorder'}
                    </Chip>
                  )}
                  {!reorderMode && <OwnerNote>Owner view</OwnerNote>}
                </>
              )}
            </ToolRow>

            {/* Keep dropzone mounted while uploading — closing on file-pick
                used to unmount progress mid-flight. */}
            {isOwner && (showUpload || uploading) && (
              <Box mb={3}>
                <UploadDropzone
                  onFiles={async (files) => {
                    await uploadFiles(files, { maxDim: uploadMaxDim })
                    setShowUpload(false)
                  }}
                  disabled={uploading}
                  progress={progress}
                />
              </Box>
            )}

            <MasonryGrid>
              {visiblePhotos.map((p) => {
                const fullIndex = photos.indexOf(p)
                return (
                  <PhotoGridTile
                    key={p.id}
                    src={p.url || p.thumbUrl || ''}
                    index={String(fullIndex + 1).padStart(3, '0')}
                    liked={p.liked}
                    likeCount={reorderMode ? undefined : (p.likeCount ?? 0)}
                    isCover={Boolean(p.egoId) && p.egoId === album.coverEgoId}
                    onSetCover={isOwner && !reorderMode && p.egoId ? () => onSetCover(p) : undefined}
                    onClick={reorderMode ? undefined : () => openLightbox(p)}
                    onLikeToggle={reorderMode ? undefined : () => toggleLike(p)}
                    onDelete={isOwner && !reorderMode ? () => requestDeletePhoto(p) : undefined}
                    draggable={reorderMode}
                    onDragStart={
                      reorderMode
                        ? (e: React.DragEvent) => {
                            setDragFromId(p.id)
                            e.dataTransfer.effectAllowed = 'move'
                          }
                        : undefined
                    }
                    onDragOver={
                      reorderMode
                        ? (e: React.DragEvent) => {
                            e.preventDefault()
                            if (dragOverId !== p.id) setDragOverId(p.id)
                          }
                        : undefined
                    }
                    onDrop={
                      reorderMode
                        ? (e: React.DragEvent) => {
                            e.preventDefault()
                            if (dragFromId) commitReorder(dragFromId, p.id)
                            setDragFromId(null)
                            setDragOverId(null)
                          }
                        : undefined
                    }
                    onDragEnd={
                      reorderMode
                        ? () => {
                            setDragFromId(null)
                            setDragOverId(null)
                          }
                        : undefined
                    }
                    sx={
                      reorderMode
                        ? {
                            cursor: 'grab',
                            opacity: dragFromId === p.id ? 0.4 : 1,
                            outline: dragOverId === p.id && dragFromId !== p.id ? '2px dashed var(--ds-accent, #be9dfd)' : 'none',
                            outlineOffset: 2,
                          }
                        : undefined
                    }
                  />
                )
              })}
            </MasonryGrid>
          </>
        )}
      </BodyWrap>

      {isOwner && photos.length > 0 && !album.galleryId && (
        <BodyWrap sx={{ pt: 0 }}>
          <PromoBanner
            eyebrow='The upgrade'
            title='Hang these on real walls.'
            description='Turn this album into a walkable 3D gallery. Same key, same guests — a whole different visit.'
            ctaLabel='Make walkable →'
            onCtaClick={handleTurnTo3D}
          />
        </BodyWrap>
      )}

      <ShareSheet
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        shareUrl={shareUrl}
        title={title}
        zIndex={1600}
        isPrivate={isOwner ? album?.privacy === 'private' : undefined}
        onTogglePrivacy={isOwner ? togglePrivacy : undefined}
        allowDownload={album?.allowDownload !== false}
        onAllowDownloadChange={isOwner ? onAllowDownloadChange : undefined}
        fullResolutionUploads={isOwner ? album?.fullResolutionUploads === true : undefined}
        onFullResolutionUploadsChange={isOwner ? onFullResolutionUploadsChange : undefined}
        uploadQualityLocked={isOwner && !isFeatureAvailable(FEATURE_KEYS.FULL_RESOLUTION_UPLOAD)}
        onUpgradeUploadQuality={() => showUpgrade({ featureKey: FEATURE_KEYS.FULL_RESOLUTION_UPLOAD })}
      />

      <Lightbox
        open={lightboxIndex != null && photos.length > 0}
        photos={photos}
        index={lightboxIndex ?? 0}
        albumId={albumId}
        albumTitle={title}
        albumOwnerId={album?.ownerId}
        canDelete={isOwner}
        allowDownload={album?.allowDownload !== false}
        coverEgoId={album?.coverEgoId}
        onSetCover={isOwner ? onSetCover : undefined}
        onClose={() => setLightboxIndex(null)}
        onIndexChange={setLightboxIndex}
        onToggleLike={(photo) => toggleLike(photo)}
        onCommentPosted={bumpCommentCount}
        onDeletePhoto={requestDeletePhoto}
        onShare={() => setShareOpen(true)}
      />

      <ConfirmDialog
        open={Boolean(photoToDelete)}
        variant={lightboxIndex != null ? 'night' : 'light'}
        title={batchCount > 1 ? 'Delete photos?' : 'Delete photo?'}
        description={
          batchCount > 1
            ? `These ${batchCount} photos were uploaded together and will all be removed.`
            : 'This photo will be permanently removed from the album.'
        }
        confirmLabel={batchCount > 1 ? `Delete ${batchCount}` : 'Delete'}
        loading={deletingPhoto}
        onConfirm={confirmDeletePhoto}
        onClose={() => !deletingPhoto && setPhotoToDelete(null)}
        zIndex={1600}
      />

      <ConfirmDialog
        open={albumDeleteOpen}
        title='Delete album?'
        description={`"${title}" and all its photos will be permanently deleted.`}
        confirmLabel='Delete album'
        loading={deletingAlbum}
        onConfirm={confirmDeleteAlbum}
        onClose={() => !deletingAlbum && setAlbumDeleteOpen(false)}
      />
    </Box>
  )
}
